import HttpRepository from './httpRepository';
import RequestRepository from './requestRepository';

import type { UiApprover } from '../components/pages/approvals/asides/forwardRequestAside';
import type { User } from '../zustandStore/slices';
import type { AxiosError, AxiosResponse } from 'axios';

// check if required ?! assigned  always returns 0
export type ApprovalState = 'assigned' | 'completed' | 'pending' | 'combinedApprovals';

type Costcentercode = {
	listId: string;
	listLabel: string;
};

type Fields = {
	amount: string;
	comments: string;
	description: string;
	endDate: string;
	frequency: string;
	ends: string;
	numberOfPayments: string;
};

type FeatureAccess = {
	reassignRequest: boolean;
	reassignRequestToSelf: boolean;
};

type Requester = {
	firstName: string;
	lastName: string;
	email: string;
	avatarUrl: string;
	initials: string;
};

type Coding = {
	costcentercode: Costcentercode;
};

export type ContextRequest = {
	attachments: any[];
	coding: Coding;
	fields: Fields;
	profileId: string;
	teamId: string;
	title: string;
	requester?: any;
};

type Context = {
	assignedUserId: string;
	features: string[];
	reassignedToSelf: boolean;
	request: ContextRequest;
	isAutoForwarded: 'true' | 'false';
	isProcessing: string;
	reassignedById?: string;
	comments: string | undefined;
};

type RequestFields = {
	amount: string;
	description: string;
	endDate: string; // fyi '2025-01-03';
};

export type ApprovalRequest = {
	amount: number;
	attachments: any[];
	businessId: string;
	cardType: string;
	coding: Coding;
	currency: string;
	fields: RequestFields;
	isArchived: boolean;
	lastEdit: string;
	profileId: string;
	requester: Requester;
	authPaddingPercentage: number;
	requestId: string;
	state: string;
	teamId: string;
	title: string;
	assignee: string;
	teamName: string;
	virtualCardId: string;
};

export type Approval = {
	assignedOn: number;
	assignees: string[];
	assigner: string;
	context: Context;
	id: string;
	state: string;
	subjectId: string;
	request: ApprovalRequest;
	exceedsApprovalLimit: boolean;
	featureAccess: FeatureAccess;
	outcome: string;
	reassignedByUser: User | null;
	completedOn: number;
	reassignedToUser: User | null;
	type: string;
	requestId?: string;
	// used in ui to know if in view conversation you are viewing an approval which is or not in my approvals list
	currentUserIsNotAssigneerByUserId?: boolean;
	// used in ui to know whci aapproval is approved
	isApproved?: boolean;
	isPending?: boolean;
	isCancelled?: boolean;
	isDeclined?: boolean;
};

export type ApprovalCardRequest = {
	cardSourceId: string;
	cardType: string;
	coding: any;
	eventId: string;
	requesterUserId: string;
	requestId: string;
	taskId: string;
	endDate: any;
};

type GetApprovalResponse = {
	count: number;
	approvals: Approval[];
};

export type ForwardRequestParams = {
	approval: Approval;
	reason: string;
	previousAssignee?: User;
	assignee: Partial<User> & UiApprover;
};

export const ApprovalRepository = {
	getApprovals: async (state: ApprovalState) => {
		const response: AxiosResponse<GetApprovalResponse, AxiosError> = await HttpRepository.tradeshiftGo().get(
			'/rest/approvals',
			{
				params: { state },
			},
		);
		return response.data;
	},
	closeStuckPendingApproval: async (stuckPendingApprovalId: string) => {
		const response = await HttpRepository.tradeshiftGo().post(`/rest/requests/${stuckPendingApprovalId}/close`);
		return response.data;
	},
	declinePendingApproval: async ({ subjectId, taskId }) => {
		const response = await HttpRepository.tradeshiftGo().post(`/rest/requests/${subjectId}/reject`, {
			taskId,
		});
		return response.data;
	},
	getTeamApprovers: async (teamId: string, approverId: string) => {
		const response = await HttpRepository.tradeshiftGo().get(`/rest/teams/${teamId}/approvers?excludeOutOfOffice=true`);
		const { users: approvers = [] } = response.data;
		const approverOptions = approvers
			?.map(({ userId: id, fullName: label, email }) => ({ id, email, label }))
			.filter(({ id }) => id !== approverId);
		return approverOptions;
	},
	forwardRequest: async ({ assignee, approval, previousAssignee, reason }: ForwardRequestParams) => {
		const requestId = approval.subjectId;
		const taskId = approval.id;
		const assigneeId = assignee.id || assignee.userId;
		const getAssignee = await HttpRepository.tradeshiftGo().get(`/rest/users/${assigneeId}`);
		const reassignRequest = await HttpRepository.tradeshiftGo().post(
			`/rest/requests/${requestId}/reassign/${assigneeId}`,
			{ previousAssignee, reason, taskId },
		);
		return { assignee: getAssignee.data, reassignee: reassignRequest.data };
	},
	cancelApproval: async ({ subjectId, virtualCardId, taskId }) => {
		if (virtualCardId) {
			const response = await HttpRepository.tradeshiftGo().post(`/rest/tasks/${subjectId}/cards/${virtualCardId}`, {
				taskId,
			});
			return response.data;
		} else {
			const requestData = await RequestRepository.getRequest({ requestId: subjectId });
			const response = await HttpRepository.tradeshiftGo().post(
				`/rest/tasks/${subjectId}/cards/${requestData.virtualCardId}`,
				{
					taskId,
				},
			);
			return response.data;
		}
	},
	approve: async (approvalRequest: ApprovalCardRequest) => {
		return await HttpRepository.tradeshiftGo().post(
			`/rest/requests/${approvalRequest.requestId}/approve`,
			approvalRequest,
		);
	},
};
